import { DOMAIN_TYPES } from "../domains/config/domainTypes";
import { normalizeTemplateDomain } from "../template/TemplateTheme.jsx";

export const SITE_CONTEXT_CACHE_VERSION = "v3";
export const SITE_CONTEXT_STORAGE_PREFIX = `academy:site-context:${SITE_CONTEXT_CACHE_VERSION}`;

const SUPPORTED_DOMAIN_TYPES = new Set([
  DOMAIN_TYPES.EDUCATION,
  DOMAIN_TYPES.RETAIL,
  DOMAIN_TYPES.FLEX_OFFICE,
  DOMAIN_TYPES.INSTITUTION,
  DOMAIN_TYPES.ASSESSMENT,
  DOMAIN_TYPES.SERVICES,
  DOMAIN_TYPES.WAAS,
  DOMAIN_TYPES.KRUMAGO,
]);

const TEMPLATE_RESOLUTION_STATUSES = new Set(["assigned", "default", "pending", "error"]);
const TEMPLATE_RESOLUTION_SOURCES = new Set(["live", "cache"]);

const safeStorage = (storage) => {
  try {
    return storage;
  } catch {
    return null;
  }
};

export const normalizeSiteHost = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  try {
    const url = rawValue.includes("://") ? new URL(rawValue) : new URL(`http://${rawValue}`);
    return url.host.toLowerCase();
  } catch {
    return rawValue.toLowerCase().replace(/\/+$/, "");
  }
};

export const getCurrentSiteHost = (location = typeof window !== "undefined" ? window.location : null) => (
  normalizeSiteHost(location?.host || location?.hostname || "")
);

export const getSiteKey = (host) => normalizeSiteHost(host);

export const getSiteContextStorageKey = (host) => (
  `${SITE_CONTEXT_STORAGE_PREFIX}:${encodeURIComponent(getSiteKey(host))}`
);

const templateCompanyIdOf = (template) => (
  template?.company?.primaryId
  || template?.companyId
  || template?.attributes?.companyId
);

const templateDomainTypeOf = (template) => (
  template?.domain?.type
  || template?.domainType
  || template?.attributes?.sourceDomain
  || ""
);

const companyDomainTypeOf = (company) => normalizeTemplateDomain(
  company?.domain?.type
  || company?.domainType
  || company?.companyDetails?.domain?.type
  || ""
);

export const getCompanyId = (company) => (
  company?.primaryId
  || company?.companyId
  || company?.companyDetails?.primaryId
  || ""
);

export const getTemplateKey = (template, company) => {
  const templateAttribute = (template?.dynamicAttributes || company?.dynamicAttributes || [])
    .find((attribute) => String(attribute?.name || "").toLowerCase().startsWith("template"));

  return template?.baseTemplateKey
    || template?.attributes?.sourceTemplate
    || template?.attributes?.retailTemplateStyle
    || template?.templateKey
    || template?.attributes?.templateKey
    || templateAttribute?.value
    || "";
};

export const getSiteContextCache = (host) => {
  const storage = safeStorage(typeof window !== "undefined" ? window.sessionStorage : null);
  if (!storage) return null;

  try {
    const raw = storage.getItem(getSiteContextStorageKey(host));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // v2 contexts did not record whether null meant "no template" or "not loaded".
    // They are intentionally rejected instead of being allowed to render a default UI.
    if (!parsed?.templateResolution) return null;
    return validateSiteContext(parsed, host);
  } catch {
    return null;
  }
};

export const setSiteContextCache = (context) => {
  const storage = safeStorage(typeof window !== "undefined" ? window.sessionStorage : null);
  if (!storage || !context?.siteKey) return;

  try {
    storage.setItem(getSiteContextStorageKey(context.siteKey), JSON.stringify(context));
  } catch {
    // Cache failures must never prevent the live site from rendering.
  }
};

export const validateSiteContext = (context, expectedHost) => {
  if (!context || typeof context !== "object") return null;

  const siteKey = getSiteKey(context.siteKey || context.hostname);
  const currentHost = getSiteKey(expectedHost || siteKey);
  const domainType = normalizeTemplateDomain(context.domainType || context.company?.domain?.type);
  const companyId = String(context.companyId || getCompanyId(context.company) || "");
  const companyStatus = String(context.company?.status || "").toUpperCase();

  if (!siteKey || !currentHost || siteKey !== currentHost) return null;
  if (!companyId || !SUPPORTED_DOMAIN_TYPES.has(domainType)) return null;
  if (companyStatus !== "ACTIVE") return null;

  const template = context.activeTemplate || null;
  const templateResolution = context.templateResolution;
  if (!templateResolution || !TEMPLATE_RESOLUTION_STATUSES.has(templateResolution.status)) return null;

  const resolutionSource = templateResolution.source || "live";
  if (!TEMPLATE_RESOLUTION_SOURCES.has(resolutionSource)) return null;
  if (templateResolution.status === "assigned" && !template) return null;
  if (templateResolution.status !== "assigned" && template) return null;

  const responseCount = templateResolution.responseCount === null
    || templateResolution.responseCount === undefined
    ? null
    : Number(templateResolution.responseCount);
  if (responseCount !== null && !Number.isFinite(responseCount)) return null;
  if (templateResolution.status === "default" && responseCount !== 0) return null;

  if (template) {
    const templateCompanyId = templateCompanyIdOf(template);
    const templateDomainType = normalizeTemplateDomain(templateDomainTypeOf(template));
    if (templateCompanyId && String(templateCompanyId) !== companyId) return null;
    if (templateDomainType && templateDomainType !== domainType) return null;
  }

  return Object.freeze({
    ...context,
    siteKey,
    hostname: siteKey,
    companyId,
    domainType,
    company: context.company || {},
    branding: context.branding || {},
    activeTemplate: template,
    templateResolution: Object.freeze({
      status: templateResolution.status,
      source: resolutionSource,
      responseCount,
      checkedAt: templateResolution.checkedAt || null,
      revalidating: Boolean(templateResolution.revalidating),
    }),
    capabilities: Object.freeze({ ...(context.capabilities || {}) }),
  });
};

export const createSiteContext = ({
  host,
  company,
  activeTemplate = null,
  capabilities = {},
  templateResolution,
}) => {
  const siteKey = getSiteKey(host);
  const domainType = companyDomainTypeOf(company);
  const companyId = String(getCompanyId(company) || "");
  const context = {
    siteKey,
    hostname: siteKey,
    companyId,
    domainType,
    company,
    branding: {
      name: company?.name || "",
      logoUrl: company?.logoImageContent?.displayLink || "",
      faviconUrl: company?.logoImageContent?.displayLink || "",
    },
    activeTemplate,
    templateResolution: templateResolution || {
      status: activeTemplate ? "assigned" : "pending",
      source: "live",
      responseCount: activeTemplate ? 1 : null,
      checkedAt: null,
      revalidating: false,
    },
    capabilities,
    templateKey: getTemplateKey(activeTemplate, company),
  };

  return validateSiteContext(context, siteKey);
};

export const isAssignedSiteContext = (context) => Boolean(
  context
  && context.templateResolution?.status === "assigned"
  && context.activeTemplate
);

export const markSiteContextRevalidating = (context) => {
  if (!isAssignedSiteContext(context)) return null;

  return validateSiteContext({
    ...context,
    templateResolution: {
      ...context.templateResolution,
      source: "cache",
      revalidating: true,
    },
  }, context.siteKey);
};

export const isSupportedDomainType = (domainType) => (
  SUPPORTED_DOMAIN_TYPES.has(normalizeTemplateDomain(domainType))
);

export const clearLegacySiteContextKeys = () => {
  if (typeof window === "undefined") return;

  ["domainType", "dynamicAttributes", "CompPrimaryId", "companyLogo", "subdomain"].forEach((key) => {
    window.localStorage.removeItem(key);
  });
};

export const writeLegacySiteContextBridge = (context) => {
  if (typeof window === "undefined" || !context) return;

  // Existing leaf screens still consume these keys. They are now a compatibility
  // mirror written only after the hostname-scoped context has been validated.
  window.localStorage.setItem("domainType", context.domainType);
  window.localStorage.setItem("CompPrimaryId", context.companyId);
  window.localStorage.setItem("subdomain", context.siteKey);
  if (context.branding?.logoUrl) {
    window.localStorage.setItem("companyLogo", context.branding.logoUrl);
  }
  if (Array.isArray(context.company?.dynamicAttributes)) {
    window.localStorage.setItem("dynamicAttributes", JSON.stringify(context.company.dynamicAttributes));
  }
};
